import { FC, useCallback, useEffect, useState } from 'react';
import PendingAccessCard from './components/Settings/PendingAccessCard';
import { useDispatch, useSelector } from 'react-redux';
import { GameRequest } from '@api/selfServe';
import {
  SELF_SERVE_PENDING_NDA_STATUS,
  SELF_SERVE_PENDING_USER_CREATION_STATUS,
  SELF_SERVE_PROCESSED_STATUS,
  SELF_SERVE_PROCESSING_STATUS,
  TOAST_TYPES,
} from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';
import { PendingAccessCardSelfServeDetails } from './types';
import { Box, Button, Typography, Tabs, Tab, Stack } from '@mui/material';
import { RootState } from '@stores/store';
import { Loader } from '@components';
import SignNDA from './components/NdaData/SignNDA';
import EditSignNDA from './components/NdaData/EditSignNDA';
import { CustomAccordion } from '@components';
import GettingStartedHeader from './components/GettingStartedHeader';
import AddIcon from '@mui/icons-material/Add';
import ApplicationOverview from './components/Settings/ApplicationOverview';
import AddGame from './components/GameData/AddGame';
import DrawerComponent from '../../../components/Drawer/Drawer';
import { pendingNDARespone } from '../MockData/pending_nda';

const GettingStarted: FC = () => {
  const dispatch = useDispatch();
  const modulesState = useSelector((state: RootState) => state?.ModulesData);
  const [openEditSignNDAConfirmation, setOpenEditSignNDAConfirmation] = useState(false);
  const [ndaDetails, setNDADetails] = useState<any>(null);
  const [openNDAForm, setOpenNDAForm] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [gameRequests, setGameRequests] = useState<GameRequest[]>([]);
  const [openAddGame, setOpenAddGame] = useState<boolean>(false);

  const [selfServeDetails, setSelfServeDetails] =
    useState<PendingAccessCardSelfServeDetails | null>(null);

  // Helper functions for status checks
  const isNDAOrUserCreationPending = Boolean(
    selfServeDetails &&
      (SELF_SERVE_PENDING_NDA_STATUS.has(selfServeDetails.requestStatus) ||
        SELF_SERVE_PENDING_USER_CREATION_STATUS.has(selfServeDetails.requestStatus)),
  );

  const isNDAComplete = Boolean(selfServeDetails && !isNDAOrUserCreationPending);

  const isAppProcessedOrProcessing = (request: GameRequest) => {
    return (
      SELF_SERVE_PROCESSING_STATUS.has(request.requestStatus) ||
      SELF_SERVE_PROCESSED_STATUS.has(request.requestStatus)
    );
  };

  const hasGameRequests = gameRequests.length > 0;

  useEffect(() => {
    if (modulesState.loadCount === 0) return;
    fetchApplicationDetails(pendingNDARespone());
  }, [modulesState]);

  const handleAddNewApp = () => {
    // setOpenAddGame(true);
  };

  const handleAddGameClose = useCallback((cancel: boolean = false) => {
    // if (!cancel) fetchApplicationDetails();
    // setOpenAddGame(false);
  }, []);

  const updateNDADetails = (
    companyName: string | null,
    companyAddress: string | null,
    signingAuthorityName: string | null,
    signingAuthorityEmail: string | null,
    signingAuthorityTitle: string | null,
  ) => {
    const hasAllRequiredFields =
      companyName &&
      companyAddress &&
      signingAuthorityName &&
      signingAuthorityEmail &&
      signingAuthorityTitle;

    if (hasAllRequiredFields) {
      setNDADetails({
        companyName,
        companyAddress,
        signingAuthorityName,
        signingAuthorityEmail,
        signingAuthorityTitle,
      });
    } else {
      setNDADetails(null);
    }
  };

  // FUNCTION TO MOCK API RESPONSE
  const fetchApplicationDetails = async (selfServeRequests: GameRequest[]): Promise<void> => {
    try {
      // const response = await fetchSelfServeDetails().unwrap();
      setGameRequests(selfServeRequests);

      if (selfServeRequests.length > 0) {
        const firstRequest = selfServeRequests[0];
        setSelfServeDetails({
          id: firstRequest.id,
          category: firstRequest.category,
          genre: firstRequest.genre,
          requestStatus: firstRequest.requestStatus,
          mmp: firstRequest.mmp,
          metadata: firstRequest.metadata,
          accessEmail: firstRequest.accessEmail,
        });

        updateNDADetails(
          firstRequest.companyName,
          firstRequest.companyAddress,
          firstRequest.signingAuthorityName,
          firstRequest.signingAuthorityEmail,
          firstRequest.signingAuthorityTitle,
        );
      }
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleCloseEditSignNDAConfirmation = (cancel: boolean) => {
    if (!cancel) {
      setOpenNDAForm(true);
    }
    setOpenEditSignNDAConfirmation(false);
  };

  const getNameFromGameRequest = (request: GameRequest) => {
    try {
      const metadata = JSON.parse(request.metadata);
      if (metadata.APP_STORE) {
        return metadata.APP_STORE.name;
      }

      if (metadata.PLAY_STORE) {
        return metadata.PLAY_STORE.name;
      }
    } catch (error) {
      return '';
    }

    return '';
  };

  // Render helper functions
  const renderAddNewAppButton = () => (
    <Button
      disableElevation
      disableFocusRipple
      disableRipple
      disableTouchRipple
      startIcon={<AddIcon />}
      color="primary"
      onClick={handleAddNewApp}
      sx={{
        fontSize: '14px',
        width: '170px',
        fontWeight: 700,
        lineHeight: '20px',
        borderRadius: '100px',
        textTransform: 'none',
        padding: '10px 24px',
      }}
    >
      Add New App
    </Button>
  );

  const renderPendingNDAMessage = () => (
    <Typography fontSize="14px" fontWeight={500} lineHeight="20px" color="#6C6C6C">
      Please sign NDA to start accessing your apps.
    </Typography>
  );

  const renderAppTabs = () => (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundColor: 'white',
            borderRadius: '12px 12px 0px 0px',
            borderBottom: '1px solid #F0F0F1',
            '.MuiTabs-indicator': {
              backgroundColor: '#51B8B0',
              height: '2px',
              borderRadius: '5px',
              overflowX: 'auto',
            },
            '.MuiTabs-scroller': {
              overflowX: 'auto !important',
              scrollbarWidth: 'thin',
            },
          }}
        >
          {gameRequests.map((request, index) => (
            <Tab
              key={request.id}
              label={`${getNameFromGameRequest(request)}`}
              id={`app-tab-${index}`}
              sx={{
                paddingY: '0px',
                height: '48px',
                minHeight: '48px',
                fontSize: '0.9rem',
                fontWeight: 700,
                textTransform: 'none',
                color: '#6C6C6C',
                '&.Mui-selected': {
                  color: '#51B8B0',
                },
                alignItems: 'center',
              }}
            />
          ))}
        </Tabs>
        <Stack direction="row" spacing={2}>
          {renderAddNewAppButton()}
        </Stack>
      </Box>
      {renderTabPanels()}
    </Box>
  );

  const renderTabPanels = () => (
    <Box role="tabpanel">
      {gameRequests.map((request, index) => (
        <Box
          key={request.id}
          role="tabpanel"
          hidden={selectedTab !== index}
          id={`app-tabpanel-${index}`}
        >
          {selectedTab === index && renderAppContent(request)}
        </Box>
      ))}
    </Box>
  );

  const renderAppContent = (request: GameRequest) => {
    if (isAppProcessedOrProcessing(request)) {
      return (
        <ApplicationOverview
          data={{
            id: request.id,
            category: request.category,
            genre: request.genre,
            requestStatus: request.requestStatus,
            mmp: request.mmp,
            metadata: request.metadata,
            accessEmail: request.accessEmail,
            companyName: ndaDetails?.companyName,
            companyAddress: ndaDetails?.companyAddress,
            signingAuthorityName: ndaDetails?.signingAuthorityName,
            signingAuthorityEmail: ndaDetails?.signingAuthorityEmail,
            signingAuthorityTitle: ndaDetails?.signingAuthorityTitle,
            accountName: request.accountName || '',
            iapMargin: request.iapMargin || '',
            connectionDetails: request.connectionDetails || '{}',
            dataSourceMetadata: request.dataSourceMetadata || '{}',
          }}
        />
      );
    }

    return (
      <PendingAccessCard
        data={{
          id: request.id,
          category: request.category,
          genre: request.genre,
          requestStatus: request.requestStatus,
          mmp: request.mmp,
          metadata: request.metadata,
          accessEmail: request.accessEmail,
        }}
        gameRequest={request}
        fetchApplicationDetails={fetchApplicationDetails}
      />
    );
  };

  const renderAppsSection = () => {
    if (!hasGameRequests) {
      return <Box>{renderAddNewAppButton()}</Box>;
    }

    if (isNDAOrUserCreationPending) {
      return (
        <>
          {renderPendingNDAMessage()}
          <Box
            sx={{
              cursor: 'not-allowed',
              pointerEvents: 'none',
              opacity: 0.5,
            }}
          >
            {renderAppTabs()}
          </Box>
        </>
      );
    }

    return renderAppTabs();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 2,
        height: '100%',
        overflow: 'auto',
      }}
    >
      <GettingStartedHeader />

      {false ? (
        <Loader />
      ) : (
        <>
          {/* NDA Section */}
          <CustomAccordion
            open={true}
            isSuccess={isNDAComplete}
            showEdit={Boolean(ndaDetails && isNDAOrUserCreationPending)}
            disableExpand={!selfServeDetails}
            onEdit={() => setOpenEditSignNDAConfirmation(true)}
            title="1. Non Disclosure Agreement"
          >
            <SignNDA
              gameRequest={gameRequests[0]}
              isProceedDisalbed={!selfServeDetails?.requestStatus}
              success={isNDAComplete}
              openNDAForm={openNDAForm}
              setOpenNDAForm={setOpenNDAForm}
              selfServeId={selfServeDetails?.id as string}
              ndaDetails={ndaDetails}
              fetchApplicationDetails={fetchApplicationDetails}
              setNDADetails={setNDADetails}
            />
          </CustomAccordion>

          {/* Apps Section */}
          <CustomAccordion open={true} hideExpand={true} title="2. Your Apps">
            {renderAppsSection()}
          </CustomAccordion>

          <EditSignNDA
            open={openEditSignNDAConfirmation}
            close={handleCloseEditSignNDAConfirmation}
          />
        </>
      )}

      {openAddGame && (
        <DrawerComponent
          anchor="right"
          open={openAddGame}
          close={(status: string) => setOpenAddGame(false)}
          height="98vh"
          zIndex={1203}
          backgroundColor="#F0F0F1"
        >
          <AddGame close={handleAddGameClose} />
        </DrawerComponent>
      )}
    </Box>
  );
};

export default GettingStarted;
